import { Button, Grid } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import * as yup from 'yup';
import { Schema } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { useCallback, useEffect, useState } from 'react';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import {
  createDocumentCategory,
  deleteDocumentCategory,
  getDocumentCategoryById,
  updateDocumentCategory,
} from 'src/shared/services/documentCategoryService';
import { toast } from 'react-toastify';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { DocumentCategory } from '../models/documentCategory';

type DocumentCategoryHandlerProps = {
  openModal: boolean;
  setOpenModal: (openModal: boolean) => void;
  setRefetchDataGrid: (refetch: boolean) => void;
  documentCategoryId?: number | null;
  setDocumentCategoryId: (categoryId: number | null) => void;
};

export const DocumentCategoryHandler = (props: DocumentCategoryHandlerProps) => {
  const { openModal, setOpenModal, setRefetchDataGrid, documentCategoryId = null, setDocumentCategoryId } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const maximumNameCharRequired = 20;
  const maximumDescCharRequired = 50;
  const maxCharNameRequiredMessage = t('common:validationMessages.maxLength', { count: maximumNameCharRequired });
  const maxCharDescRequiredMessage = t('common:validationMessages.maxLength', { count: maximumDescCharRequired });
  const schema: Schema<DocumentCategory> = yup.object().shape({
    category: yup.string().max(maximumNameCharRequired, maxCharNameRequiredMessage).required(t('common:validationMessages.required')),
    description: yup.string().max(maximumDescCharRequired, maxCharDescRequiredMessage).required(t('common:validationMessages.required')),
  });

  const formMethods = useForm<DocumentCategory>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      documentCategory_id: undefined,
      category: '',
      description: '',
    },
  });

  const { handleSubmit, reset } = formMethods;

  const handleClose = () => {
    reset({
      category: '',
      description: '',
      documentCategory_id: undefined,
    });
    setDocumentCategoryId(null);
    setOpenModal(false);
  };

  const handleDelete = async () => {
    if (documentCategoryId && documentCategoryId > 0) {
      setLoading(true);

      try {
        await deleteDocumentCategory(documentCategoryId);
        if (isMounted()) {
          toast.success(<>{t('documentArchive:documentCategory.deletedCategorySuccess')}</>);
          handleClose();
          setRefetchDataGrid(true);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      } finally {
        if (isMounted()) setLoading(false);
      }
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDocumentCategoryById(documentCategoryId || 0);

      if (isMounted()) {
        reset(res.data);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      setLoading(false);
    }
  }, [documentCategoryId, isMounted, reset]);

  useEffect(() => {
    if (isMounted() && documentCategoryId && documentCategoryId > 0) {
      fetchData();
    }
  }, [documentCategoryId, fetchData, isMounted]);

  const onSubmit = async (data: DocumentCategory) => {
    setLoading(true);

    try {
      if (!documentCategoryId) {
        await createDocumentCategory(data);
      } else {
        await updateDocumentCategory(data);
      }

      if (isMounted()) {
        if (documentCategoryId) {
          setDocumentCategoryId(null);
        }
        toast.success(
          <>
            {!documentCategoryId
              ? t('documentArchive:documentCategory.createdCategorySuccess')
              : t('documentArchive:documentCategory.updatedCategorySuccess')}
          </>,
        );
        setRefetchDataGrid(true);
        setLoading(false);
        setOpenModal(false);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      setLoading(false);
    }
  };

  const formId = 'update-document-archive-category';

  return (
    <>
      <FormProvider {...formMethods}>
        <form id={formId} onSubmit={handleSubmit(onSubmit)}>
          <GenericDialog
            disableBackdropClosing
            maxWidth='sm'
            open={openModal}
            onClose={handleClose}
            onSave={handleSubmit(onSubmit)}
            dialogTitle={t('documentArchive:createCategory')}
            dialogContent={
              <Grid container spacing={2}>
                <LoadingOverlay loading={loading} />
                <Grid item xs={6}>
                  <ControlledTextField label={t('documentArchive:documentCategory.name')} name='category' />
                </Grid>

                <Grid item xs={6}>
                  <ControlledTextField label={t('documentArchive:documentCategory.description')} name='description' />
                </Grid>
              </Grid>
            }
            dialogActions={
              <Grid container justifyContent={documentCategoryId ? 'space-between' : 'flex-end'}>
                {documentCategoryId && documentCategoryId > 0 && (
                  <Grid item>
                    <Button variant='outlined' color='error' onClick={() => setOpenDeleteDialog(true)}>
                      {t('common:button.delete')}
                    </Button>
                  </Grid>
                )}
                <Grid item>
                  <Button form={formId} sx={{ marginLeft: 2 }} disabled={loading} disableElevation variant='contained' color='primary' type='submit'>
                    {t('common:button.save')}
                  </Button>
                </Grid>
              </Grid>
            }
          />
        </form>
      </FormProvider>
      <DeleteConfirmationDialog
        title={t('common:confirmDelete')}
        value=''
        modaltext={t('common:irreversibleAction')}
        onClose={(value) => {
          if (value) {
            handleDelete();
          } else {
            setOpenDeleteDialog(false);
          }
        }}
        open={openDeleteDialog}
      />
    </>
  );
};
